import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { useTranslation } from './LocalizationProvider';
import {
  formatDistance,
  formatTime,
  formatCoordinate,
} from '../util/formatter';
import { useAttributePreference, usePreference } from '../util/preferences';

const ExportDialog = ({ open, handleClose, devices }) => {
  const t = useTranslation();
  const distanceUnit = useAttributePreference('distanceUnit');
  const coordinateFormat = usePreference('coordinateFormat');
  const hours12 = usePreference('twelveHourFormat');

  // Get positions from Redux state
  const positions = useSelector((state) => state.session.positions);

  // Check if position data is available
  const positionsAvailable = useMemo(() => devices.some((device) => positions &&
    typeof positions === 'object' &&
    positions[device.id] &&
    (positions[device.id].latitude || positions[device.id].longitude)), [devices, positions]);

  const [selectedFields, setSelectedFields] = useState({
    // Device section
    name: true,
    id: false,
    status: false,
    lastUpdate: false,
    installTime: false,
    impoundTime: false,

    // Customer section
    customer_name: true,
    customer_address: true,
    customer_phone: false,

    // Vehicle section
    vehicle_make: true,
    vehicle_model: true,
    vehicle_year: true,
    vehicle_plate: false,
    vehicle_vin: true,
    vehicle_state: false,

    // Position section
    position: false,
    totalDistance: false,
    thirtyDayDistance: false,
  });

  const [fileName, setFileName] = useState('device_export');

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    const newSelectedFields = {};

    // Set all fields to the same value
    Object.keys(selectedFields).forEach((key) => {
      newSelectedFields[key] = isChecked;
    });

    setSelectedFields(newSelectedFields);
  };

  const handleFieldChange = (field) => {
    setSelectedFields({
      ...selectedFields,
      [field]: !selectedFields[field],
    });
  };

  const formatExportValue = (device, fieldName, value) => {
    if (value === undefined || value === null) return '';

    switch (fieldName) {
      case 'lastUpdate':
      case 'installTime':
      case 'impoundTime':
        return formatTime(value, 'minutes', hours12);
      case 'position':
        return value.latitude && value.longitude
          ? `${formatCoordinate('latitude', value.latitude, coordinateFormat)}, ${formatCoordinate('longitude', value.longitude, coordinateFormat)}`
          : '';
      case 'distance':
        return value ? formatDistance(value, distanceUnit, t) : '';
      default:
        return String(value);
    }
  };

  const handleExport = () => {
    // Prepare data for export
    const exportData = devices.map((device) => {
      const deviceData = {};

      // Find position for this device with proper error handling
      let position = {};
      try {
        if (positions && typeof positions === 'object') {
          position = positions[device.id] || {};
        }
      } catch (error) {
        position = {}; // Fallback to empty object
      }

      // Device fields
      if (selectedFields.name) deviceData[t('sharedName')] = device.name || '';
      if (selectedFields.id) deviceData[t('deviceIdentifier')] = device.uniqueId || '';
      if (selectedFields.status) deviceData[t('deviceStatus')] = device.status || '';
      if (selectedFields.lastUpdate) deviceData[t('deviceLastUpdate')] = formatExportValue(device, 'lastUpdate', device.lastUpdate);
      if (selectedFields.installTime) deviceData[t('deviceInstallTime')] = formatExportValue(device, 'installTime', device.installTime);
      if (selectedFields.impoundTime) deviceData[t('sharedImpound')] = formatExportValue(device, 'impoundTime', device.impoundTime);

      // Customer fields - these are direct fields, not in attributes
      if (selectedFields.customer_name) deviceData[`${t('sharedCustomer')} ${t('sharedName')}`] = device.contact || '';
      if (selectedFields.customer_address) deviceData[`${t('sharedCustomer')} ${t('sharedAddress')}`] = device.address || '';
      if (selectedFields.customer_phone) deviceData[`${t('sharedCustomer')} ${t('sharedPhone')}`] = device.phone || '';

      // Vehicle fields - direct fields, not in attributes
      if (selectedFields.vehicle_vin) deviceData[t('vehicleVIN')] = device.vin || '';
      if (selectedFields.vehicle_make) deviceData[t('vehicleMake')] = device.vehicleMake || '';
      if (selectedFields.vehicle_model) deviceData[t('vehicleModel')] = device.vehicleModel || '';
      if (selectedFields.vehicle_year) deviceData[t('vehicleYear')] = device.vehicleYear || '';
      if (selectedFields.vehicle_plate) deviceData[t('vehiclePlate')] = device.vehiclePlate || '';
      if (selectedFields.vehicle_state) deviceData[t('vehicleState')] = device.vehicleState || '';

      // Position fields - with careful null checks
      if (selectedFields.position && position && position.latitude && position.longitude) {
        deviceData[t('sharedLocation')] = formatExportValue(device, 'position', position);
      }

      if (selectedFields.totalDistance && position && position.attributes) {
        deviceData[t('sharedTraveled')] = formatExportValue(device, 'distance', position.attributes.totalDistance);
      }

      if (selectedFields.thirtyDayDistance && device.thirtyDayDistance) {
        deviceData[t('sharedLast30Days')] = formatExportValue(device, 'distance', device.thirtyDayDistance);
      }

      return deviceData;
    });

    // Convert to CSV
    if (exportData.length === 0) {
      return;
    }

    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...exportData.map((row) => headers.map((field) => {
        const value = row[field];
        return value !== undefined && value !== null ? `"${String(value).replace(/"/g, '""')}"` : '';
      }).join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('deviceExport')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('sharedFileName')}
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  onChange={handleSelectAll}
                  checked={Object.values(selectedFields).every((value) => value)}
                  indeterminate={Object.values(selectedFields).some((value) => value) && !Object.values(selectedFields).every((value) => value)}
                />
              )}
              label={<Typography variant="subtitle1" fontWeight="bold">{t('sharedSelectAll')}</Typography>}
            />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              {t('sharedDevice')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.name} onChange={() => handleFieldChange('name')} />
                )}
                label={t('sharedName')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.id} onChange={() => handleFieldChange('id')} />
                )}
                label={t('deviceIdentifier')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.status} onChange={() => handleFieldChange('status')} />
                )}
                label={t('deviceStatus')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.lastUpdate} onChange={() => handleFieldChange('lastUpdate')} />
                )}
                label={t('deviceLastUpdate')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.installTime} onChange={() => handleFieldChange('installTime')} />
                )}
                label={t('deviceInstallTime')}
              />
              {/* <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.impoundTime} onChange={() => handleFieldChange('impoundTime')} />
                )}
                label={t('sharedImpound')}
              /> */}
            </FormGroup>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              {t('sharedCustomer')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.customer_name} onChange={() => handleFieldChange('customer_name')} />
                )}
                label={t('sharedName')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.customer_address} onChange={() => handleFieldChange('customer_address')} />
                )}
                label={t('sharedAddress')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.customer_phone} onChange={() => handleFieldChange('customer_phone')} />
                )}
                label={t('sharedPhone')}
              />
            </FormGroup>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              {t('sharedVehicle')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_vin} onChange={() => handleFieldChange('vehicle_vin')} />
                )}
                label={t('vehicleVIN')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_make} onChange={() => handleFieldChange('vehicle_make')} />
                )}
                label={t('vehicleMake')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_model} onChange={() => handleFieldChange('vehicle_model')} />
                )}
                label={t('vehicleModel')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_year} onChange={() => handleFieldChange('vehicle_year')} />
                )}
                label={t('vehicleYear')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_plate} onChange={() => handleFieldChange('vehicle_plate')} />
                )}
                label={t('vehiclePlate')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.vehicle_state} onChange={() => handleFieldChange('vehicle_state')} />
                )}
                label={t('vehicleState')}
              />
              <FormControlLabel
                control={(
                  <Checkbox checked={selectedFields.thirtyDayDistance} onChange={() => handleFieldChange('thirtyDayDistance')} />
                )}
                label={t('sharedLast30Days')}
              />
            </FormGroup>

            {positionsAvailable && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                  {t('sharedPosition')}
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={(
                      <Checkbox checked={selectedFields.position} onChange={() => handleFieldChange('position')} />
                    )}
                    label={t('sharedLocation')}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox checked={selectedFields.totalDistance} onChange={() => handleFieldChange('totalDistance')} />
                    )}
                    label={t('sharedTraveled')}
                  />
                </FormGroup>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('sharedCancel')}
        </Button>
        <Button onClick={handleExport} color="primary" variant="contained">
          {t('sharedExport')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
